import { Controller } from '@hotwired/stimulus'
import { GRID_EVENTS } from './grid_events'

const RELOAD_DEBOUNCE_MS = 400

// The "Needs your attention" rail beside a grid mid-upload. Clicking a card scrolls the grid to its
// cell; Fix value jumps into the cell to edit it; Skip is a form post that reloads this frame.
// Its listeners are data-actions on the rail element (see UploadReviewRailComponent). What must outlive
// a reload (tab, scroll, active card, focus) is kept on the turbo-frame, which a reload doesn't replace.
export default class extends Controller {
  static values = { reloadPath: String, resolved: Object, decisionsCount: Number }

  connect() {
    this.frameElement = this.element.closest('turbo-frame')
    this.panels = this.element.querySelector('.tab-list__panels')
    this.runPendingReload()
    this.announceResolvedCell()
    this.restoreActiveCard()
    this.restoreScroll()
    this.restoreFocus()
    // The rail reconnects with a fresh count each time it reloads after a resolve — tell the header
    // "Review" badge so it stays in sync.
    document.dispatchEvent(
      new CustomEvent(GRID_EVENTS.reviewCounts, { detail: { decisions: this.decisionsCountValue } })
    )
  }

  disconnect() {
    if (this.reloadTimeout) this.frameElement?.setAttribute('data-reload-pending', '')
    clearTimeout(this.reloadTimeout)
  }

  // A cell edited in the grid re-classifies it, so reload the rail to update the counts and tabs.
  scheduleReload() {
    clearTimeout(this.reloadTimeout)
    this.reloadTimeout = setTimeout(() => {
      this.reloadTimeout = null
      this.reload()
    }, RELOAD_DEBOUNCE_MS)
  }

  // A reload still waiting when the last response swapped this content in may have been for a save that
  // response didn't see, so run it now.
  runPendingReload() {
    if (!this.frameElement?.hasAttribute('data-reload-pending')) return

    this.frameElement.removeAttribute('data-reload-pending')
    this.reload()
  }

  // So a reload after a decision returns to the picked tab rather than the server's default, "All".
  rememberTab(event) {
    const tab = event.target.closest('[role="tab"]')
    if (tab && this.frameElement) this.frameElement.dataset.activeTab = this.tabName(tab)
  }

  tabName(tab) {
    return tab.id.replace(/^tab-/, '').replace(/-mobile$/, '')
  }

  rememberScroll(event) {
    if (event.target === this.panels && this.frameElement) {
      this.frameElement.dataset.railScrollTop = String(this.panels.scrollTop)
    }
  }

  restoreScroll() {
    const top = this.frameElement?.dataset.railScrollTop
    if (this.panels && top) requestAnimationFrame(() => (this.panels.scrollTop = Number(top)))
  }

  // A card action reloads this frame and drops focus; remember the card to refocus it.
  rememberCardFocus(event) {
    const card = event.target.closest('.grid-review-rail__card-actions')?.closest('.grid-review-rail__card')
    if (!card || !this.frameElement) return

    this.frameElement.dataset.focusCellKey = card.dataset.cellKey
    this.frameElement.dataset.focusIndex = String(this.visibleCards().indexOf(card))
  }

  visibleCards() {
    const panel = this.element.querySelector('[role="tabpanel"]:not([hidden])') || this.element
    return [...panel.querySelectorAll('.grid-review-rail__card[data-cell-key]')]
  }

  // Only when focus fell to the body, so it never steals focus.
  restoreFocus() {
    const frame = this.frameElement
    if (!frame?.dataset.focusCellKey) return

    const { focusCellKey: key, focusIndex } = frame.dataset
    delete frame.dataset.focusCellKey
    delete frame.dataset.focusIndex
    if (document.activeElement && document.activeElement !== document.body) return

    const cards = this.visibleCards()
    const card = cards.find(c => c.dataset.cellKey === key) || cards[Number(focusIndex)] || cards[cards.length - 1]
    const target =
      card?.querySelector('.grid-review-rail__card-actions button') ||
      card ||
      this.element.querySelector('.grid-review-rail__title')
    target?.focus()
  }

  // The tab the person is on (the one they last clicked, else the live tablist's selected tab), so a
  // reload can stay on it instead of snapping back to "All". Skip posts carry it as a form param.
  activeTabName() {
    const remembered = this.frameElement?.dataset.activeTab
    if (remembered) return remembered

    const tab =
      this.element.querySelector('.tab-list__desktop [role="tab"][aria-selected="true"]') ||
      this.element.querySelector('[role="tab"][aria-selected="true"]')
    return tab?.id ? this.tabName(tab) : null
  }

  // Click (or Enter on) a card, but not its action buttons, to mark it active and scroll the grid to
  // its cell.
  reveal(event) {
    if (event.target.closest('.grid-review-rail__card-actions')) return

    this.revealCard(event.currentTarget.closest('.grid-review-rail__card'))
  }

  revealCard(card) {
    const cell = this.cellFrom(card)
    if (!cell) return

    this.markActive(card)
    document.dispatchEvent(new CustomEvent(GRID_EVENTS.revealCell, { detail: cell }))
  }

  // Highlight one card and remember which cell it is, so a rail reload re-applies the highlight.
  markActive(card) {
    this.element
      .querySelectorAll('.grid-review-rail__card--active')
      .forEach(c => c.classList.remove('grid-review-rail__card--active'))
    if (!card) return

    card.classList.add('grid-review-rail__card--active')
    if (this.frameElement) this.frameElement.dataset.activeCellKey = card.dataset.cellKey || ''
  }

  restoreActiveCard() {
    const key = this.frameElement?.dataset.activeCellKey
    if (!key) return

    const card = this.element.querySelector(`.grid-review-rail__card[data-cell-key="${CSS.escape(key)}"]`)
    card?.classList.add('grid-review-rail__card--active')
  }

  // Decide: scroll to the cell and open the decision modal, where the value is placed or corrected.
  decide(event) {
    event.preventDefault()
    const cell = this.cellFrom(event.currentTarget)
    if (cell) document.dispatchEvent(new CustomEvent(GRID_EVENTS.decideCell, { detail: cell }))
  }

  cellFrom(element) {
    const card = element.closest('[data-cell-key]')
    return card ? { cellKey: card.dataset.cellKey, day: Number(card.dataset.day) } : null
  }

  // This render is the response to a Skip: tell the grid to restamp the settled cell's mark, since
  // the grid still holds the old resolution in its row data.
  announceResolvedCell() {
    const resolved = this.hasResolvedValue ? this.resolvedValue : null
    if (resolved && resolved.cellKey) {
      document.dispatchEvent(new CustomEvent(GRID_EVENTS.cellResolved, { detail: resolved }))
    }
  }

  reload() {
    if (!this.frameElement || !this.hasReloadPathValue) return

    const url = new URL(this.reloadPathValue, window.location.origin)
    const tab = this.activeTabName()
    if (tab) url.searchParams.set('active_tab', tab)
    // A new src loads the frame by itself; reload() only when it's unchanged, so it's fetched once.
    if (this.frameElement.getAttribute('src') === url.toString()) this.frameElement.reload()
    else this.frameElement.src = url.toString()
  }
}
